// VirtualFormTrackingPlugin
//
// @snowplow/browser-plugin-form-tracking only instruments real <form>
// elements. Chat widgets, wizards, and React components built from plain
// divs never fire native `submit`, so form tracking silently never runs.
//
// This module emits the same Snowplow event (submit_form - same schema,
// same field shapes) for any container a
// caller-supplied resolver recognizes, so downstream consumers of those
// events don't change. It only depends on @snowplow/browser-tracker (the
// same package the rest of the tracker uses), so it stays usable standalone.
// Delegated entirely off `document`, so - unlike native form tracking -
// no MutationObserver is needed to notice containers added later.

import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';

const DEFAULT_SCHEMAS = {
  submit: 'iglu:com.snowplowanalytics.snowplow/submit_form/jsonschema/1-0-0'
};

const FIELD_SELECTOR = 'input, textarea, select';
const DEFAULT_SUBMIT_SELECTOR = 'button, input[type="submit"], input[type="image"]';

function isTrackableElement(el) {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  );
}

function getFieldType(el) {
  if (el instanceof HTMLInputElement) return (el.type || 'text').toLowerCase();
  return el.tagName.toLowerCase();
}

// The submit_form schema constrains `type`/
// `elementType` to the native HTML <input> type enum (text, email, radio,
// etc.) - "select"/"textarea" aren't valid values there. Native Snowplow
// form tracking only ever sets this field for actual <input> elements and
// omits it entirely for <select>/<textarea> (JSON.stringify drops
// `undefined` keys, matching that same omission rather than substituting
// a misleading "text").
function getSchemaFieldType(el) {
  return el instanceof HTMLInputElement ? getFieldType(el) : undefined;
}

// Inside a real <form>: only native enableFormTracking() should track it.
// Outside one: only this plugin should. Checked against the raw event
// target (not a resolved trigger/container) so it's decided before any
// other resolution work runs, and can't be bypassed by a custom
// containerResolver/selector picking some other wrapper.
function isNativeFormEvent(target) {
  return target instanceof Element && target.closest('form') !== null;
}

function getClasses(el) {
  return el.classList && el.classList.length ? Array.from(el.classList) : undefined;
}

// A virtual container rarely has an id the way a real <form> would - fall
// back to other conventions before giving up on identifying it at all.
function getFormId(container) {
  return (
    container.id ||
    container.getAttribute('data-form-id') ||
    container.getAttribute('name') ||
    ''
  );
}

// Fields that should never appear in a submit payload at all -
// not even with a null value. Disabled fields aren't part of a native form
// submission either way; hidden/file/submit/image carry no meaningful
// user-entered value worth tracking.
function isSkippableField(el) {
  if (el.disabled) return true;
  const type = getFieldType(el);
  return type === 'hidden' || type === 'file' || type === 'submit' || type === 'image';
}

// <select multiple> - .value only ever returns the first selected option,
// so multi-selects need .selectedOptions instead. Joined into one string
// since transform()/the event schema both expect a single string value.
function getRawValue(el) {
  if (el instanceof HTMLSelectElement && el.multiple) {
    return Array.from(el.selectedOptions).map((o) => o.value).join(',');
  }
  return el.value;
}

// Mirrors the native plugin's own redaction: password is never sent, and an
// unchecked checkbox/radio is sent as null (not omitted) - same as native
// Snowplow form tracking's actual behavior, not raw browser submission
// semantics (which would drop the field entirely). The caller's own
// filter/transform (e.g. isFieldAllowed / FormFieldEncryption) then layers
// on top of that.
function getFieldValue(el, filter, transform) {
  const type = getFieldType(el);

  if (type === 'password') return null;
  if ((type === 'checkbox' || type === 'radio') && !el.checked) return null;
  if (filter && !filter(el)) return null;

  const rawValue = getRawValue(el);
  return transform ? transform(rawValue, el, el) : rawValue;
}

function track(schemas, key, data) {
  trackSelfDescribingEvent({ event: { schema: schemas[key], data } });
}

// Walk up from `el` preferring an ancestor with more than one form control -
// works for any DOM shape, no class names or attributes required. This is
// the true last resort, after the more intentional markers below.
//
// Prefers *more than one* field over just one: component libraries (MUI's
// FormControl, Quasar's q-field, etc.) commonly wrap every individual field
// in its own private div, and that immediate wrapper trivially "contains a
// field" - itself. Stopping there would resolve a different container per
// field instead of the one shared container for the whole logical form,
// silently splitting a single form's fieldCache tracking across N
// separate per-field containers (symptom: submit_form reporting only the
// last field touched, since every other field was cached under a container
// nobody else shared) - so the walk keeps climbing past a lone-field
// ancestor looking for a shared one first.
//
// But a widget can genuinely have only one field (a chat box's message
// input), where no ancestor will ever contain more than one - climbing all
// the way to document.body and returning null there would leave a
// perfectly real single-field widget with no container at all. So the
// nearest single-field ancestor is remembered as a fallback and returned
// if the climb never finds anything better.
function findAncestorWithFields(el) {
  let node = el;
  let singleFieldFallback = null;

  while (node && node !== document.body) {
    if (node.querySelectorAll) {
      const fieldCount = node.querySelectorAll(FIELD_SELECTOR).length;
      if (fieldCount > 1) return node;
      if (fieldCount === 1 && !singleFieldFallback) singleFieldFallback = node;
    }
    node = node.parentElement;
  }

  return singleFieldFallback;
}

// Last-resort guess when neither containerResolver nor selector is given:
// a real <form>, then the common stable-marker conventions a customer might
// already be using, then the generic structural fallback above - in order
// of how likely each is to be an intentional marker vs. an inference.
//
// `allowStructuralFallback` gates that last, weakest guess. It's a
// reasonable inference from a *field* (the element itself proves it lives
// inside something form-like) but not from a submit trigger: a plain
// <button> - matched by DEFAULT_SUBMIT_SELECTOR - proves nothing about
// intent, so "shares a big ancestor with some unrelated field" is enough to
// misfire on e.g. a "Return To Site" nav button that happens to sit in the
// same layout section as an unrelated search/newsletter input.
function defaultContainerResolver(el, allowStructuralFallback) {
  return (
    el.closest('form') ||
    el.closest('[data-form]') ||
    el.closest('[data-sp-form]') ||
    el.closest('[role="form"]') ||
    (allowStructuralFallback ? findAncestorWithFields(el) : null)
  );
}

// containerResolver takes priority if supplied, then selector, then the
// default guesses above - so this plugin degrades gracefully instead of
// silently tracking nothing for callers who configure neither.
function resolveContainerGetter(options) {
  const { containerResolver, selector } = options;

  return (el, allowStructuralFallback = true) => {
    if (!el || !el.closest) return null;

    return (
      (containerResolver ? containerResolver(el) : null) ??
      (selector ? el.closest(selector) : null) ??
      defaultContainerResolver(el, allowStructuralFallback)
    );
  };
}

function resolveSubmitTriggerGetter(options) {
  if (typeof options.submitResolver === 'function') {
    return options.submitResolver;
  }
  if (typeof options.submitSelector === 'string') {
    const submitSelector = options.submitSelector;
    return (el) => (el && el.closest ? el.closest(submitSelector) : null);
  }
  return (el) => (el && el.closest ? el.closest(DEFAULT_SUBMIT_SELECTOR) : null);
}

// Collapses same-name radio/checkbox entries into a single element - only
// the checked radio, or a comma-joined list of checked checkbox values -
// instead of one array entry per input (some with a redundant null value).
// This deliberately diverges from native Snowplow form tracking, which
// keeps one entry per input; only applied when options.consolidateGroups
// is explicitly turned on.
function consolidateGroups(entries) {
  const groups = new Map();
  const result = [];

  entries.forEach((entry) => {
    const isGroupable = (entry.type === 'radio' || entry.type === 'checkbox') && entry._groupName;
    if (!isGroupable) {
      result.push(stripInternal(entry));
      return;
    }

    if (!groups.has(entry._groupName)) {
      groups.set(entry._groupName, {
        name: entry._groupName,
        nodeName: entry.nodeName,
        type: entry.type,
        values: []
      });
    }
    if (entry._checked) {
      groups.get(entry._groupName).values.push(entry.value);
    }
  });

  groups.forEach(({ name, nodeName, type, values }) => {
    const value = type === 'radio'
      ? (values.length ? values[0] : null)
      : (values.length ? values.join(',') : null);

    result.push({ name, nodeName, type, value });
  });

  return result;
}

function stripInternal({ _groupName, _checked, ...rest }) {
  return rest;
}

/**
 * Enable submit tracking for any container a resolver
 * recognizes as a logical "form". Container resolution tries, in order:
 * 1. `options.containerResolver(el)` - full control, e.g. chaining
 *    `el.closest(".wizard") ?? el.closest(".chat-widget")`
 * 2. `options.selector` via `el.closest(selector)`
 * 3. `<form>`, then `[data-form]`, then `[data-sp-form]`, then `[role="form"]`
 *    - common stable-marker conventions, tried in that order
 * 4. the nearest ancestor that contains any input/select/textarea - a
 *    purely structural guess requiring no class names or attributes at all
 * so a caller who configures neither still gets reasonable default behavior
 * instead of silently tracking nothing.
 *
 * Step 4 only runs when resolving a container from a *field* element
 * (Enter): the field itself is evidence it lives inside something
 * form-like. It's skipped when resolving a submit trigger's own container
 * (click), since a generic <button> - matched by the default
 * submitSelector - proves nothing about intent; without an explicit
 * marker/resolver, "shares a big ancestor with some unrelated field" would
 * misfire on ordinary buttons (e.g. a "Return To Site" nav button that
 * happens to sit in the same layout section as an unrelated search input).
 *
 * Submit-trigger resolution follows the same shape:
 * `options.submitResolver(el)`, then `options.submitSelector`, then the
 * built-in default (`button`, `input[type=submit]`, `input[type=image]` -
 * matching native browser submit-trigger semantics only; non-standard
 * attributes like `[data-submit]` are opt-in via `submitSelector`, since
 * different sites may reuse them for buttons that aren't actually submits).
 *
 * Returns a disposer that removes all listeners this call added - call it
 * before re-enabling (e.g. in tests, or if init can run more than once) to
 * avoid stacking duplicate document-level listeners.
 *
 * @param options.selector - CSS selector for the virtual form container (tried after containerResolver)
 * @param options.containerResolver - (el) => Element|null, tried before selector
 * @param options.submitResolver - (el) => Element|null, tried before submitSelector
 * @param options.submitSelector - CSS selector for what counts as a submit trigger (tried after submitResolver, before the built-in default)
 * @param options.shouldSubmit - (event) => boolean, final say on whether a detected submit trigger/Enter actually submits (e.g. to ignore Enter inside an autocomplete widget)
 * @param options.consolidateGroups - collapse radio/checkbox groups to one entry per name instead of one per input (default false, matches native Snowplow's own behavior)
 * @param options.filter - (el) => boolean, same contract as native fields.filter
 * @param options.transform - (value, el, el) => value, same contract as native fields.transform
 * @param options.schemas - { submit } schema URI override
 * @returns {() => void} disposer that removes all listeners added by this call
 */
export function enableVirtualFormTracking(options = {}) {
  console.log("Virtual tracking initialized");
  const getContainer = resolveContainerGetter(options);
  const getSubmitTrigger = resolveSubmitTriggerGetter(options);
  const shouldSubmit = typeof options.shouldSubmit === 'function' ? options.shouldSubmit : () => true;
  const filter = options.filter;
  const transform = options.transform;
  const schemas = Object.assign({}, DEFAULT_SCHEMAS, options.schemas);

  // Guards against a single physical interaction firing submit twice (e.g.
  // a button that's both focused and Enter-pressed in the same moment).
  const submitting = new WeakSet();

  // The virtual form the user was last actually focused in - internal state
  // only, never emits an event on its own. A submit trigger's own DOM
  // position can't use structural fallback at all (see the "Return To
  // Site" note in onClick below), so without this a click has no way to
  // resolve a container unless the widget carries an explicit
  // <form>/[data-form]/[data-sp-form]/[role="form"] marker - which most
  // marker-less chat widgets/wizards don't. Falling back to whatever the
  // user was just interacting with covers that gap.
  let lastActiveContainer = null;

  const onFocusIn = (e) => {
    if (isNativeFormEvent(e.target)) return;

    const target = e.target;
    if (!isTrackableElement(target) || isSkippableField(target)) return;

    const container = getContainer(target);
    if (!container || container instanceof HTMLFormElement) return;

    lastActiveContainer = container;
  };

  // container -> Map(field element -> last-known field payload). Submitting
  // the same container repeatedly (e.g. a wizard's "Next") reuses cached
  // filter/transform results instead of recomputing them for every field on
  // every submit; only fields that changed (or the one being actively
  // edited right now) get recomputed.
  const fieldCache = new WeakMap();

  function getCache(container) {
    let cache = fieldCache.get(container);
    if (!cache) {
      cache = new Map();
      fieldCache.set(container, cache);
    }
    return cache;
  }

  function cacheField(container, el) {
    if (isSkippableField(el)) return;
    const type = getSchemaFieldType(el);
    getCache(container).set(el, {
      name: el.id || el.name || '',
      value: getFieldValue(el, filter, transform),
      nodeName: el.nodeName,
      type,
      // internal-only, used for radio/checkbox consolidation, stripped before emit
      _groupName: el.name || undefined,
      _checked: (type === 'radio' || type === 'checkbox') ? el.checked : undefined
    });
  }

  function collectFields(container) {
    const cache = getCache(container);

    // Catch up the field the user is still typing in - if they hit Enter
    // before blurring, its last `change` (if any) is stale.
    const active = document.activeElement;
    if (active && isTrackableElement(active) && getContainer(active) === container) {
      cacheField(container, active);
    }

    // Anything not yet cached - untouched but pre-filled fields, or fields
    // added to the container since it was first seen - still needs a live
    // read. Already-cached fields are trusted as-is.
    Array.from(container.querySelectorAll(FIELD_SELECTOR)).forEach((el) => {
      if (isSkippableField(el) || cache.has(el)) return;
      cacheField(container, el);
    });

    const entries = Array.from(cache.values());
    return options.consolidateGroups
      ? consolidateGroups(entries)
      : entries.map(stripInternal);
  }

  function submitContainer(container) {
    console.log("submitContainer called");
    if (submitting.has(container)) return;
    submitting.add(container);

    try {
      track(schemas, 'submit', {
        formId: getFormId(container),
        formClasses: getClasses(container),
        elements: collectFields(container)
      });

      fieldCache.delete(container);
      if (lastActiveContainer === container) {
        lastActiveContainer = null;
      }
    } finally {
      // Guarantees the container isn't stuck "submitting" forever if
      // track()/collectFields() throws before this would otherwise run.
      requestAnimationFrame(() => submitting.delete(container));
    }
  }

  // Click handles the Send-button case.
  const onClick = (e) => {
    if (isNativeFormEvent(e.target)) return;

    console.log("Document click", e.target);

    const trigger = getSubmitTrigger(e.target);
    console.log("Trigger:", trigger);
    if (!trigger) return;

    // Structural fallback (nearest ancestor with *any* field) is disabled
    // here: a button isn't itself evidence of being inside a form-like
    // widget the way a field is, so without an explicit marker/resolver a
    // generic button (e.g. "Return To Site") must not be treated as a
    // submit just because it shares a large ancestor with an unrelated field.
    // Falls back to whatever the user was last actually focused in - the
    // trigger's own DOM position alone can't resolve most marker-less
    // widgets (a single-field chat box, a wizard's "Next" button in a
    // separate footer), so without this fallback a click has no way to
    // find its container at all on those.
    const container = getContainer(trigger, false) || lastActiveContainer;
    console.log("Container:", container);
    if (!container) return;

    // Belt-and-suspenders: also catches the default resolver's own case.
    if (container instanceof HTMLFormElement) return;

    if (!shouldSubmit(e)) return;

    submitContainer(container);
  };

  // Enter is handled separately from click: on keydown the target is the
  // input/textarea itself, not a button, so a single shared handler keyed
  // off "is this a submit trigger" would never fire for Enter at all.
  const onKeyDown = (e) => {
    if (e.key !== 'Enter' || e.isComposing) return;
    if (isNativeFormEvent(e.target)) return;

    const target = e.target;
    // Enter in a <textarea> means newline, not submit
    if (!isTrackableElement(target) || target instanceof HTMLTextAreaElement) return;
    if (isSkippableField(target)) return;

    const container = getContainer(target) || lastActiveContainer;
    if (!container) return;

    if (container instanceof HTMLFormElement) return;

    if (!shouldSubmit(e)) return;

    submitContainer(container);
  };

  document.addEventListener('focusin', onFocusIn, true);
  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKeyDown, true);

  return function disableVirtualFormTracking() {
    document.removeEventListener('focusin', onFocusIn, true);
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('keydown', onKeyDown, true);
  };
}
